import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';
import {
  AfterViewInit,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  combineLatest,
  fromEvent,
} from 'rxjs';
import {
  debounceTime,
  delay,
  distinctUntilChanged,
  map,
  startWith,
} from 'rxjs/operators';
import { DomainKey, Product } from '../../../shared/types/product';
import { ResponsiveService } from '../services/responsive.service';
import { SearchService } from '../services/search.service';
import { SidenavService } from '../services/sidenav.service';
import * as T from '../../../shared/translations';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import {
  AFFILIATE_ENABLED,
  DOMAIN_KEYS,
} from '../../../shared/constants';
import { EventService } from '../services/event.service';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ProductRowComponent } from '../product-row/product-row.component';
import { AmazonAdsComponent } from '../amazon-ads/amazon-ads.component';
import { LivefeedMiniComponent } from '../livefeed-mini/livefeed-mini.component';
import { LivefeedService } from '../services/livefeed.service';
import { NgStyle } from '@angular/common';

@Component({
    selector: 'app-product-grid',
    templateUrl: './product-grid.component.html',
    styleUrls: ['./product-grid.component.scss'],
    standalone: true,
    imports: [
      ReactiveFormsModule,
      ScrollingModule,
      MatFormFieldModule,
      MatInputModule,
      MatIconModule,
      MatButtonModule,
      MatTooltipModule,
      ProductRowComponent,
      AmazonAdsComponent,
      LivefeedMiniComponent,
      NgStyle,
      RouterLink,
    ]
})
export class ProductGridComponent implements AfterViewInit {
  readonly viewport = viewChild.required(CdkVirtualScrollViewport);
  private firstLoad = true;
  height = window.matchMedia('(max-width: 509px)').matches ? '130' : '336';
  private readonly baseCardWidth = 248;
  readonly showScrollUpButton = signal(false);
  readonly columns = signal(1);
  readonly cardWidth = signal(232);
  readonly productRows = computed(() =>
    this.chunk(this.searchService.products(), this.columns())
  );
  T = T;
  domain: DomainKey = '1';
  affiliateEnabled = AFFILIATE_ENABLED;
  searchControl = new FormControl('');
  private readonly destroyRef = inject(DestroyRef);
  private readonly isSmallScreen$ = toObservable(this.responsiveService.isSmallScreen);

  constructor(
    private router: Router,
    readonly searchService: SearchService,
    private sidenavService: SidenavService,
    private responsiveService: ResponsiveService,
    private readonly activatedRoute: ActivatedRoute,
    private eventService: EventService,
    private livefeedService: LivefeedService,
  ) {
    this.activatedRoute.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        const domainParam = params['domain'];
        if (domainParam && DOMAIN_KEYS.indexOf(domainParam) >= 0) {
          this.domain = domainParam;
        } else {
          this.domain = '1';
        }
        this.livefeedService.switchDomain(this.domain);
      });
    this.eventService.resetForms$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.searchControl.setValue('');
      });
    this.activatedRoute.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        const searchParam = params.search;
        if (searchParam && typeof searchParam === 'string') {
          this.searchControl.setValue(searchParam);
        }
      });

    // Scroll to top when loading completes
    effect(() => {
      const loading = this.searchService.getProductsLoading();
      if (!loading) {
        this.scrollToTopIfViewportReady();
      }
    });
  }

  private scrollToTopIfViewportReady() {
    try {
      const vp = this.viewport?.();
      if (vp) {
        vp.scrollToIndex(0);
        this.showScrollUpButton.set(false);
      }
    } catch {
      // viewport not yet initialized
    }
  }

  ngAfterViewInit() {
    combineLatest([
      this.isSmallScreen$,
      fromEvent(window, 'resize').pipe(startWith(undefined)),
      this.sidenavService.activity$,
    ])
      .pipe(
        delay(0),
        debounceTime(250),
        takeUntilDestroyed(this.destroyRef),
        map(([isSmall]) => {
          const viewportWidth = this.viewport().getElementRef().nativeElement.clientWidth;
          if (isSmall) {
            this.height = '130';
            return 1;
          }
          this.height = '336';
          const columns = Math.floor(
            (viewportWidth - 14) /
            this.baseCardWidth
          );
          const dynamicCardWidth = Math.floor((viewportWidth - 14) / columns - 16);
          this.cardWidth.set(dynamicCardWidth);
          return columns;
        }),
        distinctUntilChanged()
      )
      .subscribe((columns) => {
        this.columns.set(columns);
        this.viewport().checkViewportSize();
      });

    this.viewport()
      .elementScrolled()
      .pipe(debounceTime(100), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.showScrollUpButton.set(
          this.viewport().measureScrollOffset() > 500
        );
      });
  }

  maybeGetNextProducts() {
    const range = this.viewport().getRenderedRange();
    const total = this.viewport().getDataLength();
    if (this.firstLoad) {
      this.firstLoad = false;
      return;
    }
    if (range.end === total) {
      this.loadNextPage();
    }
  }

  loadNextPage() {
    this.searchService.getNextPage();
  }

  scrollToTop() {
    this.viewport().scrollToIndex(0);
    this.showScrollUpButton.set(false);
  }

  chunk(arr: Product[], n: number): Product[][] {
    const chunked: Product[][] = arr.reduce(
      (chunk: Product[][], val) => {
        if (chunk[chunk.length - 1].length === n) {
          chunk.push([]);
        }
        chunk[chunk.length - 1].push(val);
        return chunk;
      },
      [[]]
    );
    return chunked;
  }

  doSearch() {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { search: this.searchControl.value },
      queryParamsHandling: 'merge',
    });
  }

}
